import { useJobillaOrgId } from '@/hooks'
import {
	CampaignGenerateMutationResponse,
	useCampaignCreateFromAtsJob,
	useCampaignGenerate,
	useCampaignScrape,
} from '@jobilla/nexus-sdk'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { z } from 'zod'
import { usePositionStorage, useSupplyBrandingLibrary } from '../../../../../hooks'
import { NewModeEditor, NewModeEditorProps } from '../../../../../ui'

const campaignNewSearchSchema = z.object({
	position: z.string().optional(),
	url: z.string().optional(),
	scrapeError: z.string().optional(),
})

export const Route = createFileRoute('/_auth/company/$companyId/campaign/new')({
	validateSearch: campaignNewSearchSchema,
	component: RouteComponent,
})

function RouteComponent() {
	const navigate = useNavigate()
	const search = Route.useSearch()
	const { companyId } = Route.useParams()
	const { store } = useSupplyBrandingLibrary({ enableSleeping: false })
	const jobillaOrgId = useJobillaOrgId()
	const { clearPosition, clearUrl } = usePositionStorage()

	useEffect(() => {
		if (search.url) {
			clearUrl()
		}
		if (search.position) {
			clearPosition()
		}
	}, [search.url, search.position, clearUrl, clearPosition])

	// Priority: scrapeError (failed scrape → manual) > url (scan) > position (manual)
	const initialValues = search.scrapeError
		? {
			url: search.url,
			mode: 'manual' as const,
			scrapeError: search.scrapeError,
		}
		: search.url
		? {
			url: search.url,
			mode: 'scan' as const,
		}
		: search.position
		? {
			title: search.position,
			location: 'Remote',
			language: 'en-GB',
			requirements: `Looking for a ${search.position}`,
			mode: 'manual' as const,
		}
		: undefined

	const { mutateAsync: generateFromScanMutation } = useCampaignScrape({
		mutationKey: ['generate-from-scan'],
	})

	const { mutateAsync: generateFromAtsJobMutation } = useCampaignCreateFromAtsJob({
		mutationKey: ['generate-from-ats-job'],
	})

	const { mutateAsync: generateFromManualMutation } = useCampaignGenerate({
		mutationKey: ['generate-from-manual'],
		onSuccess: (data: CampaignGenerateMutationResponse) => {
			void navigate({
				to: '/company/$companyId/campaign/$campaignId',
				params: {
					companyId,
					campaignId: data.id,
				},
			})
		},
	})

	function successNavigation(uuid: string) {
		void navigate({
			to: '/company/$companyId/campaign/$campaignId',
			params: {
				companyId,
				campaignId: uuid,
			},
		})
	}

	const props = {
		onGenerateFromScan: async (uuid: string, url, brandId, generateKeyImage) => {
			await generateFromScanMutation({
				data: {
					url,
					organisationId: jobillaOrgId,
					brandingId: brandId,
					campaignId: uuid,
					generateKeyImage,
				},
			})
			successNavigation(uuid)
		},
		onGenerateFromAtsJob: async (uuid: string, atsJob, brandId, generateKeyImage) => {
			await generateFromAtsJobMutation({
				data: {
					organisationId: jobillaOrgId,
					exportIntegrationId: atsJob.exportIntegrationId,
					atsJobId: atsJob.atsJobId,
					brandingId: brandId,
					campaignId: uuid,
					generateKeyImage,
				},
			})
			successNavigation(uuid)
		},
		onGenerateFromManual: async (uuid: string, data, brandId, generateKeyImage) => {
			await generateFromManualMutation({
				data: {
					organisationId: jobillaOrgId,
					id: uuid,
					title: data.title,
					requirements: data.requirements,
					location: data.location,
					brandingId: brandId,
					language: data.language,
					generateKeyImage,
				},
			})
		},
		organisationId: jobillaOrgId,
		initialValues,
	} as NewModeEditorProps

	return <NewModeEditor {...props} brandStore={store} />
}
